/**
 * Starts the SwissPass OIDC authorize redirect (authorization code + PKCE).
 */

import { getSwissPassConfig } from "../FrontendConfigApi";
import { setPkceTransient } from "./auth-session.helpers";
import { getOpenIdConfiguration } from "./openid-discovery.service";
import {
    computeCodeChallengeS256,
    generateCodeVerifier,
    generateOAuthState,
} from "./pkce.helpers";

/**
 * SwissPass authorization-server id extracted from a discovery or authorize URL.
 * Paths look like `/authorization-servers/<id>/` or `/oauth2/v3/<id>/authorize`.
 *
 * @param url - Absolute SwissPass URL.
 * @returns Server id when present, otherwise null.
 */
function extractSwissPassServerId( url: string ): string | null {
    const fromAuthorizationServers = url.match( /\/authorization-servers\/([^/?#]+)/u );
    if ( fromAuthorizationServers?.[1] != null ) {
        return fromAuthorizationServers[1];
    }
    const fromOauthAuthorize = url.match( /\/oauth2\/v3\/([^/?#]+)\/authorize/u );
    if ( fromOauthAuthorize?.[1] != null ) {
        return fromOauthAuthorize[1];
    }

    return null;
}

/**
 * Rejects a client_id that does not belong to the discovered authorization server.
 * SwissPass treats the server id as the client; a mismatch yields a generic
 * "access to the requested application is not possible" error on the IdP UI.
 *
 * @param clientId - Configured OAuth client id.
 * @param configurationLink - Discovery document URL.
 * @param authorizationEndpoint - Authorize endpoint from discovery.
 */
function assertClientMatchesAuthorizationServer(
    clientId: string,
    configurationLink: string,
    authorizationEndpoint: string,
): void {
    const serverId = extractSwissPassServerId( authorizationEndpoint ) ??
      extractSwissPassServerId( configurationLink );
    if ( serverId != null && serverId !== clientId ) {
        throw new Error( `SwissPass client_id "${ clientId }" does not match authorization server "${ serverId }"` );
    }
}

/**
 * Persists PKCE material and redirects the browser to SwissPass authorize.
 *
 * @returns Resolves once navigation is triggered (page unload follows).
 */
export async function startSwissPassLogin(): Promise<void> {
    const env = getSwissPassConfig();
    const discovery = await getOpenIdConfiguration( env.configurationLink, true );
    assertClientMatchesAuthorizationServer(
        env.clientId,
        env.configurationLink,
        discovery.authorizationEndpoint,
    );
    const codeVerifier = generateCodeVerifier();
    const state = generateOAuthState();
    const codeChallenge = await computeCodeChallengeS256( codeVerifier );

    setPkceTransient( codeVerifier, state );

    const authorizeUrl = new URL( discovery.authorizationEndpoint );
    authorizeUrl.searchParams.set( "client_id", env.clientId );
    authorizeUrl.searchParams.set( "response_type", "code" );
    authorizeUrl.searchParams.set( "scope", "openid customer" );
    authorizeUrl.searchParams.set( "state", state );
    authorizeUrl.searchParams.set( "redirect_uri", env.redirectUri );
    authorizeUrl.searchParams.set( "code_challenge_method", "S256" );
    authorizeUrl.searchParams.set( "code_challenge", codeChallenge );
    authorizeUrl.searchParams.set( "lang", "fr" );

    window.location.assign( authorizeUrl.toString() );
}
